
import React from 'react';
import { Control } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '../ui/InfoTooltip';
import { FormCard } from '../ui/FormCard';

interface PersonalStatusSectionProps {
  control: Control<any>;
  applicantStatus: string;
}

export function PersonalStatusSection({ control, applicantStatus }: PersonalStatusSectionProps) {
  return (
    <FormCard title="Personal Status" description="Tell us about your household situation">
      <div className="space-y-4">
        <FormField
          control={control}
          name="applicantStatus"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel className="flex items-center gap-2">
                Applicant Status
                <InfoTooltip content="Your marital/partnership status affects how your finances are assessed under Fair Deal. Couples are assessed on half of their combined income and assets." />
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="single" id="single" />
                    <FormLabel htmlFor="single" className="font-normal">Single individual</FormLabel>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="couple" id="couple" />
                    <FormLabel htmlFor="couple" className="font-normal">
                      Couple (married, civil partners, or cohabiting for 3+ years)
                    </FormLabel>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormDescription>
                {applicantStatus === 'couple' 
                  ? "As a couple, you'll be assessed on 50% of your combined income and assets."
                  : "As a single person, you'll be assessed on 100% of your income and assets."}
              </FormDescription>
            </FormItem>
          )}
        />

        {applicantStatus === 'couple' && (
          <FormField
            control={control}
            name="dependentSpouse"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    My spouse/partner will continue to live at home
                    <InfoTooltip content="Fair Deal guarantees that your spouse/partner at home keeps at least 50% of your combined income or the equivalent of the State Pension (whichever is higher)." />
                  </FormLabel>
                  <FormDescription>
                    This ensures your partner at home is financially protected.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        )}

        <FormField
          control={control}
          name="otherDependents"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  I have dependent family members
                  <InfoTooltip content="Certain maintenance payments or support for dependents may be deductible from your assessable income." />
                </FormLabel>
                <FormDescription>
                  Such as children or other relatives who are financially dependent on you.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>
    </FormCard>
  );
}
